import {useUser} from '@common/auth/ui/use-user';
import {Trans} from '@ui/i18n/trans';
import {Button} from '@ui/buttons/button';
import {useResendVerificationEmail} from '@common/auth/requests/use-resend-verification-email';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';
import {useSettings} from '@ui/settings/use-settings';
import {useLogout} from '@common/auth/requests/logout';
import {Form} from '@ui/forms/form';
import {Controller, useForm} from 'react-hook-form';
import {TextField} from '@ui/forms/input-field/text-field/text-field';
import {useTrans} from '@ui/i18n/use-trans';
import {message} from '@ui/i18n/message';
import {KeyboardArrowLeftIcon} from '@ui/icons/material/KeyboardArrowLeft';
import {
  useValidateEmailVerificationOtp,
  ValidateEmailVerificationOtpPayload,
} from '@common/auth/requests/use-validate-email-verification-otp';
import {createBrowserRouter} from 'react-router';
import {FullPageLoader} from '@ui/progress/full-page-loader';
import {getAssetUrl} from '@ui/utils/urls/get-asset-url';
import React, {useEffect, useState} from 'react';

export const verifyEmailRouter = createBrowserRouter([
  {
    path: '*',
    element: <EmailVerificationPage />,
    hydrateFallbackElement: <FullPageLoader screen />,
  },
]);

// otp codes expire 30min after being generated, see OtpCode::createForEmailVerification
const OTP_LIFETIME_MS = 30 * 60 * 1000;

function useOtpCountdown() {
  const [expiresAt, setExpiresAt] = useState(() => Date.now() + OTP_LIFETIME_MS);
  const [remainingMs, setRemainingMs] = useState(() => expiresAt - Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingMs(Math.max(0, expiresAt - Date.now()));
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const minutes = Math.floor(remainingMs / 60000);
  const seconds = Math.floor((remainingMs % 60000) / 1000);

  return {
    isExpired: remainingMs <= 0,
    formatted: `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`,
    reset: () => setExpiresAt(Date.now() + OTP_LIFETIME_MS),
  };
}

export function EmailVerificationPage() {
  const {trans} = useTrans();
  const {data} = useUser('me');
  const resendEmail = useResendVerificationEmail();
  const {
    branding: {logo_light, logo_dark},
  } = useSettings();
  const isDarkMode = useIsDarkMode();
  const logoSrc = isDarkMode ? logo_light : logo_dark;
  const logout = useLogout();

  const form = useForm<ValidateEmailVerificationOtpPayload>();
  const validateOtp = useValidateEmailVerificationOtp(form);
  const otpCountdown = useOtpCountdown();

  return (
    <div className="flex min-h-screen w-screen bg-alt p-24">
      <div className="mx-auto mt-40 max-w-440">
        <Button
          variant="outline"
          onClick={() =>
            logout.mutate(undefined, {
              onSuccess: () => {
                window.location.href = '/';
              },
            })
          }
          disabled={logout.isPending}
          startIcon={<KeyboardArrowLeftIcon />}
          size="xs"
          className="mb-54 mr-auto"
        >
          <Trans message="Logout" />
        </Button>
        {logoSrc && (
          <img
            src={getAssetUrl(logoSrc)}
            alt="Site logo"
            className="mx-auto mb-44 block h-42 w-auto"
          />
        )}
        <div className="text-center">
          <h1 className="mb-24 text-3xl">
            <Trans message="Verify your email" />
          </h1>
          <h2 className="text-lg">
            <Trans
              message="Enter the verification code we sent to :email"
              values={{email: maskEmailAddress(data?.user.email)}}
            />
          </h2>
          <Form
            form={form}
            onSubmit={values => validateOtp.mutate(values)}
            className="my-16"
          >
            <Controller
              name="code"
              control={form.control}
              rules={{ required: true }}
              render={({field: {onChange, ...field}, fieldState}) => (
                <TextField
                  {...field}
                  onChange={e =>
                    onChange(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  invalid={fieldState.invalid}
                  errorMessage={fieldState.error?.message}
                  label={<Trans message="Code" />}
                  placeholder={trans(
                    message('Enter your verification code'),
                  )}
                  autoFocus
                  autoComplete="one-time-code"
                  autoCorrect="off"
                  autoCapitalize="off"
                  maxLength={6}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              )}
            />
            <div className="mt-8 text-sm text-muted">
              {otpCountdown.isExpired ? (
                <Trans message="Code has expired, please request a new one" />
              ) : (
                <Trans
                  message="Code expires in :time"
                  values={{time: otpCountdown.formatted}}
                />
              )}
            </div>
            <Button
              type="submit"
              variant="flat"
              color="primary"
              size="md"
              className="mt-24 w-full"
              disabled={validateOtp.isPending}
            >
              <Trans message="Next" />
            </Button>
          </Form>
          <div className="mb-24 text-sm">
            <Trans
              message="If you don't see the email in your inbox, check your spam folder and promotions tab. If you still don't see it, <a>request a resend</a>."
              values={{
                a: text => (
                  <Button
                    variant="link"
                    color="primary"
                    disabled={resendEmail.isPending || !data?.user.email}
                    onClick={() => {
                      resendEmail.mutate(
                        {email: data!.user.email},
                        {onSuccess: () => otpCountdown.reset()},
                      );
                    }}
                  >
                    {text}
                  </Button>
                ),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function maskEmailAddress(email: string | undefined) {
  if (!email) return '*******************';
  const [username, domain] = email.split('@');
  return `${username.slice(0, 2)}****@${domain}`;
}
